/**
 * Utility functions for route detection and pathname handling
 */

import { delocalizeRoute } from '../constants/url-constants';

/**
 * List of all tools page routes
 */
export const TOOLS_ROUTES = [
  '/tools/job-flow-sankey',
  '/tools/viewed-jobs',
  '/tools/saved-jobs',
  '/tools/applied-jobs',
  '/tools/interviewing-jobs',
  '/tools/offer-jobs',
  '/tools/rejected-jobs',
  '/tools/hired-jobs',
  '/tools/hidden-jobs',
  '/tools/saved-searches',
] as const;

function stripLeadingLocale(pathname: string): { path: string; localeHint: 'en' | 'fi' | 'sv' | null } {
  const clean = pathname.split('?')[0];
  const segments = clean.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first === 'en' || first === 'fi' || first === 'sv') {
    const rest = segments.slice(1).join('/');
    return { path: rest ? `/${rest}` : '/', localeHint: first };
  }
  return { path: clean.startsWith('/') ? clean : `/${clean}`, localeHint: null };
}

/**
 * Canonical English tools path (e.g. `/tools/saved-jobs`) for any localized or legacy tools URL.
 */
export function englishCanonicalToolsPathFromPathname(pathname: string): string | null {
  const { path, localeHint } = stripLeadingLocale(pathname);
  const normalized = path.length > 1 ? path.replace(/\/+$/, '') : path;

  for (const route of TOOLS_ROUTES) {
    if (normalized === route) return route;
  }

  const localesToTry: Array<'en' | 'fi' | 'sv'> = localeHint ? [localeHint] : ['en', 'fi', 'sv'];
  for (const loc of localesToTry) {
    const english = delocalizeRoute(normalized, loc).replace(/\/+$/, '');
    for (const route of TOOLS_ROUTES) {
      if (english === route) return route;
    }
  }
  return null;
}

/**
 * Checks if a given pathname is a tools page route
 * Handles both localized and non-localized paths
 */
export function isToolsPage(pathname: string): boolean {
  return englishCanonicalToolsPathFromPathname(pathname) != null;
}

/**
 * Gets the current tool route from a pathname
 * Returns the normalized route (without locale) or null if not a tools page
 */
export function getCurrentToolRoute(pathname: string): string | null {
  return englishCanonicalToolsPathFromPathname(pathname);
}

/**
 * Gets the viewMode for tools pages, normalizing 'map' to 'list' since map view
 * is not available on tools pages.
 * @param defaultMode - The default mode to use if no stored value or invalid value
 * @returns 'grid' | 'list' (never 'map')
 */
export function getToolsPageViewMode(defaultMode: 'grid' | 'list' = 'list'): 'grid' | 'list' {
  if (typeof window === 'undefined') return defaultMode;

  // Check both 'layout' and 'jobcrawls.viewMode' for backward compatibility
  const stored = localStorage.getItem('layout') || localStorage.getItem('jobcrawls.viewMode');

  // If stored value is 'map', normalize to 'list' since map view is not available on tools pages
  if (stored === 'map') {
    // Update localStorage to reflect the normalized value
    try {
      localStorage.setItem('layout', 'list');
      localStorage.setItem('jobcrawls.viewMode', 'list');
    } catch (err) {
      console.warn('[route-utils] Failed to update viewMode in localStorage:', err);
    }
    return 'list';
  }

  // Return stored value if it's valid for tools pages, otherwise return default
  if (stored === 'list' || stored === 'grid') {
    return stored as 'grid' | 'list';
  }

  return defaultMode;
}
